import apiService from './api';
import { isNoDataError, isHmsSubscriptionInactiveError } from '../utilities/errorHelper';

class NotificationService {
  constructor() {
    this.notifications = [];
    this.isLoading = false;
    this.lastFetched = null;
    this.cacheKey = 'BOOKING_NOTIFICATIONS';
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes
    this.pageLoadKey = 'NOTIFICATION_PAGE_LOAD';
    this.listeners = []; // For reactive updates
    
    // Check if this is a page refresh
    this.handlePageRefresh();
  }

  // Subscribe to notification updates
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  // Notify all listeners of changes
  notify() {
    this.listeners.forEach(callback => {
      try {
        callback(this.notifications);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  // Handle page refresh detection
  handlePageRefresh() {
    const lastPageLoad = sessionStorage.getItem(this.pageLoadKey);
    const now = Date.now();
    
    // If no last page load or it's been more than 1 second, consider it a page refresh
    if (!lastPageLoad || (now - parseInt(lastPageLoad)) > 1000) {
      // Clear in-memory cache on page refresh
      this.notifications = [];
      this.lastFetched = null;
    }
    
    // Update page load timestamp
    sessionStorage.setItem(this.pageLoadKey, now.toString());
  }

  // Get notifications from cache or API
  async getNotifications(forceRefresh = false) {
    // Check if this is a page refresh (first call after page load)
    const isPageRefresh = this.isPageRefresh();
    
    // If force refresh or page refresh, clear cache and fetch fresh
    if (forceRefresh || isPageRefresh) {
      this.notifications = [];
      this.lastFetched = null;
      // Clear localStorage cache on page refresh
      if (isPageRefresh) {
        try {
          localStorage.removeItem(this.cacheKey);
        } catch (error) {
          console.error('Error clearing cache on page refresh:', error);
        }
      }
      return await this.fetchNotifications();
    }

    // Return cached data if available in memory and cache is valid
    if (this.notifications.length > 0 && this.isCacheValid()) {
      return this.notifications;
    }

    // Return cached data from localStorage if available
    const cached = this.getCachedNotifications();
    if (cached && this.isCacheValid(cached.timestamp)) {
      this.notifications = cached.data;
      this.lastFetched = cached.timestamp;
      this.notify();
      return this.notifications;
    }

    // Fetch from API
    return await this.fetchNotifications();
  }

  // Check if this is a page refresh
  isPageRefresh() {
    const lastPageLoad = sessionStorage.getItem(this.pageLoadKey);
    const now = Date.now();
    
    // If no last page load or it's been more than 1 second, consider it a page refresh
    if (!lastPageLoad || (now - parseInt(lastPageLoad)) > 1000) {
      // Update page load timestamp
      sessionStorage.setItem(this.pageLoadKey, now.toString());
      return true;
    }
    
    return false;
  }

  // Fetch notifications from API
  async fetchNotifications() {
    if (this.isLoading) {
      // If already loading, return existing data
      return this.notifications;
    }

    try {
      this.isLoading = true;
      const response = await apiService.getBookingNotifications();
      this.notifications = response.data || [];
      this.lastFetched = Date.now();
      
      // Cache in localStorage
      this.cacheNotifications(this.notifications, this.lastFetched);
      
      // Notify all listeners
      this.notify();
      
      return this.notifications;
    } catch (error) {
      if (isHmsSubscriptionInactiveError(error)) {
        this.lastFetched = Date.now();
        this.notify();
        return this.notifications;
      }
      if (isNoDataError(error)) {
        this.notifications = [];
        this.lastFetched = Date.now();
        this.notify();
        return this.notifications;
      }
      console.error('Error fetching booking notifications:', error);
      // Return cached data on error
      const cached = this.getCachedNotifications();
      if (cached) {
        this.notifications = cached.data;
        this.lastFetched = cached.timestamp;
        this.notify();
        return this.notifications;
      }
      this.notify();
      return [];
    } finally {
      this.isLoading = false;
    }
  }

  // Get notification count for a specific object
  getNotificationCount(objectId) {
    if (!objectId || !this.notifications.length) return 0;
    const notification = this.notifications.find(n => n.objectId === objectId);
    return notification ? parseInt(notification.count) || 0 : 0;
  }

  // Get new booking IDs for a specific object
  getNewBookingIds(objectId) {
    if (!objectId || !this.notifications.length) return [];
    const notification = this.notifications.find(n => n.objectId === objectId);
    if (notification && notification.newBookingIds) {
      return Array.isArray(notification.newBookingIds) 
        ? notification.newBookingIds 
        : [];
    }
    return [];
  }

  // Refresh notifications (called after marking as read)
  async refreshNotifications() {
    return await this.fetchNotifications();
  }

  // Clear cache
  clearCache() {
    this.notifications = [];
    this.lastFetched = null;
    try {
      localStorage.removeItem(this.cacheKey);
    } catch (error) {
      console.error('Error clearing notification cache:', error);
    }
  }

  // Check if cache is valid
  isCacheValid(timestamp = this.lastFetched) {
    if (!timestamp) return false;
    return (Date.now() - timestamp) < this.cacheDuration;
  }

  // Cache notifications in localStorage
  cacheNotifications(data, timestamp) {
    try {
      localStorage.setItem(this.cacheKey, JSON.stringify({
        data,
        timestamp
      }));
    } catch (error) {
      console.error('Error caching notifications:', error);
    }
  }

  // Get cached notifications from localStorage
  getCachedNotifications() {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (error) {
      console.error('Error reading cached notifications:', error);
    }
    return null;
  }
}

// Export singleton instance
export default new NotificationService();
